/**
 * memoryCompressionEngine.js — FASE R5 core humanization engine.
 *
 * Takes data from all existing memory stores and compresses it into a
 * ≤200 char warm pastoral fragment. This is the key piece: raw data
 * goes in, human-sounding Spanish comes out.
 *
 * Output philosophy:
 *   "Ha estado cargando esto por mucho tiempo."       ✓
 *   "La conversación anterior terminó con esperanza cautelosa."  ✓
 *   "Previous continuity state indicates prolonged depletion."   ✗
 *   "On April 7, the user expressed emotional exhaustion."       ✗
 *
 * Max output: 200 chars. 1-2 sentences max. No timestamps. No labels.
 *
 * Priority order:
 *  1. Active crisis / acute grief / escalation
 *  2. Recurring exhaustion / stable_heavy
 *  3. Unresolved pastoral tension
 *  4. Restoration progress (fragile or improving)
 *  5. Milestone note (one-time historical moment)
 *  6. null — nothing worth surfacing
 */

import { buildEmotionalContinuity }     from "./emotionalContinuityTracker";
import { mapToPhrase, mapMilestoneToPhrase } from "./pastoralTrajectoryMap";
import { getMilestones }                from "../kevia-continuity/spiritualMilestoneTracker";
import { loadEmotionalMemory }          from "./relationalMemoryEngine";

const MAX_FRAGMENT_CHARS = 200;

// Tension phrases — phrased as warm relational observations
const TENSION_PHRASES = {
  aislamiento_pastoral: "La soledad en el ministerio sigue siendo real.",
  sobrecarga_central:   "Sigue cargando mucho más de lo que debería solo.",
  conflicto_liderazgo:  "Esa tensión con el liderazgo todavía no se ha resuelto.",
  tension_familiar:     "La situación familiar sigue siendo difícil.",
};

// Care intensity phrases — only injected for high/crisis to signal active pastoral urgency
const CARE_INTENSITY_PHRASES = {
  crisis:   "Hay una situación que requiere atención pastoral real.",
  high:     "Esta persona necesita seguimiento cercano.",
};

/**
 * Build the compressed memory fragment for an entity.
 *
 * @param {{
 *   entityId:   string,
 *   entityType: string,
 * }} entity
 * @returns {{
 *   fragment:   string|null,
 *   hasMemory:  boolean,
 *   anchor:     string|null,
 *   direction:  string,
 * }}
 */
export function buildMemoryFragment(entity) {
  const nullResult = { fragment: null, hasMemory: false, anchor: null, direction: "unknown" };
  if (!entity?.entityId) return nullResult;

  // Read emotional continuity (reads from spiritualJourneyMemory + relationalMemoryEngine)
  const continuity = buildEmotionalContinuity(entity);
  const stored     = loadEmotionalMemory(entity.entityId);

  // If nothing significant in continuity and no stored anchor, exit early
  if (!continuity.isSignificant && !stored?.emotionalAnchor && !stored?.milestoneNote) {
    return nullResult;
  }

  const parts = [];

  // Priority 1: Care intensity (crisis/high signals active pastoral need)
  const careLine = CARE_INTENSITY_PHRASES[stored?.careIntensity];
  if (stored?.careIntensity === "crisis" || stored?.careIntensity === "high") {
    if (careLine) parts.push(careLine);
  }

  // Priority 2: Emotional trajectory phrase
  if (continuity.isSignificant && continuity.anchor && continuity.direction !== "unknown") {
    const trajectoryPhrase = mapToPhrase(continuity.anchor, continuity.direction, continuity.recurrenceCount);
    if (trajectoryPhrase) parts.push(trajectoryPhrase);
  } else if (stored?.emotionalAnchor && !continuity.isSignificant) {
    // Stored anchor but not enough journal history for direction — use light recall
    const staticPhrase = mapToPhrase(stored.emotionalAnchor, "recurring", stored.recurrenceCount ?? 0);
    if (staticPhrase && stored.recurrenceCount >= 2) parts.push(staticPhrase);
  }

  // Priority 3: Unresolved tension (if not already covered by trajectory phrase)
  if (stored?.unresolvedTension && parts.length === 0) {
    const tensionPhrase = TENSION_PHRASES[stored.unresolvedTension];
    if (tensionPhrase) parts.push(tensionPhrase);
  }

  // Priority 4: Milestone note (only if nothing else to say)
  if (parts.length === 0 && stored?.milestoneNote) {
    const milestonePhrase = mapMilestoneToPhrase(stored.milestoneNote);
    if (milestonePhrase) parts.push(milestonePhrase);
  }

  // Fallback: recent milestones from milestone tracker (if no other content)
  if (parts.length === 0) {
    const milestones = getMilestones(entity.entityId);
    if (milestones.length > 0) {
      const latest = milestones[0];
      const mPhrase = mapMilestoneToPhrase(latest.type);
      if (mPhrase) parts.push(mPhrase);
    }
  }

  if (parts.length === 0) return nullResult;

  // Compose — take max 2 sentences, cap at MAX_FRAGMENT_CHARS
  const raw      = parts.slice(0, 2).join(" ");
  const fragment = raw.length > MAX_FRAGMENT_CHARS
    ? raw.slice(0, MAX_FRAGMENT_CHARS - 1) + "."
    : raw;

  return {
    fragment,
    hasMemory: true,
    anchor:    continuity.anchor ?? stored?.emotionalAnchor ?? null,
    direction: continuity.direction,
  };
}
